import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import HikeCard from '../components/HikeCard';
import { API_URL, HIKE_PAGE_ONLY } from '../config';
import Spinner from '../components/Spinner';
import Footer from '../components/Footer';

const AVAILABLE_CATEGORIES = [
  'Escursionismo',
  'Alpinismo',
  'Trekking',
  'Ferrata',
  'Invernale',
  'Sci alpinismo',
  'Sci di fondo',
  'MTB',
  'Appennino',
  'Alpi',
  'Giornaliera',
  'Weekend',
  'Più giorni',
  'Rifugio',
  'Giovani',
  'Arrampicata',
  'Hotel',
  'Under 25'
];

// Italian month names for date search
const ITALIAN_MONTHS = {
  'gennaio': 0, 'gen': 0,
  'febbraio': 1, 'feb': 1,
  'marzo': 2, 'mar': 2,
  'aprile': 3, 'apr': 3,
  'maggio': 4, 'mag': 4,
  'giugno': 5, 'giu': 5,
  'luglio': 6, 'lug': 6,
  'agosto': 7, 'ago': 7,
  'settembre': 8, 'set': 8,
  'ottobre': 9, 'ott': 9,
  'novembre': 10, 'nov': 10,
  'dicembre': 11, 'dic': 11
};

export default function HomePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, token, isFavorite, hasPermission } = useAuth();
  const [hikes, setHikes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize filters from URL query parameters
  const [selectedCategories, setSelectedCategories] = useState(() => {
    const cats = searchParams.get('categories');
    return cats ? cats.split(',').filter(cat => AVAILABLE_CATEGORIES.includes(cat)) : [];
  });
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(() => {
    return searchParams.get('favorites') === 'true';
  });
  const [showHidden, setShowHidden] = useState(() => {
    return searchParams.get('hidden') === 'true';
  });
  const [searchQuery, setSearchQuery] = useState(() => {
    return searchParams.get('search') || '';
  });
  const [calendarLoading, setCalendarLoading] = useState(false);

  const fetchHikes = useCallback(async () => {
    try {
      // Single API call - backend automatically enriches response with user context if authenticated
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      // Only use /hikes/all if showHidden AND user is authenticated (has token)
      const useHiddenEndpoint = showHidden && token;
      const endpoint = useHiddenEndpoint ? `${API_URL}/hikes/all` : `${API_URL}/hikes`;
      const response = await fetch(endpoint, { headers });

      if (!response.ok) {
        throw new Error('Impossibile caricare le gite');
      }

      const data = await response.json();
      setHikes(data);
      // Done! Backend already included is_favorited and participation_status in each hike
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, showHidden]);

  const updateURLParams = useCallback(({ categories, favorites, hidden, search }) => {
    const params = new URLSearchParams(searchParams);

    // Update categories
    if (categories !== undefined) {
      if (categories.length > 0) {
        params.set('categories', categories.join(','));
      } else {
        params.delete('categories');
      }
    }

    // Update favorites
    if (favorites !== undefined) {
      if (favorites) {
        params.set('favorites', 'true');
      } else {
        params.delete('favorites');
      }
    }

    // Update hidden
    if (hidden !== undefined) {
      if (hidden) {
        params.set('hidden', 'true');
      } else {
        params.delete('hidden');
      }
    }

    // Update search
    if (search !== undefined) {
      if (search && search.trim()) {
        params.set('search', search.trim());
      } else {
        params.delete('search');
      }
    }

    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  // Reset showHidden when user loses permission
  useEffect(() => {
    if (!hasPermission('hike:view_hidden:any') && showHidden) {
      setShowHidden(false);
      updateURLParams({ hidden: false });
    }
  }, [hasPermission, showHidden, updateURLParams]);

  useEffect(() => {
    fetchHikes();
  }, [fetchHikes]);

  // Check if home page access is restricted - must be after all hooks
  if (HIKE_PAGE_ONLY && !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-sm w-full">
          <h2 className="text-lg font-semibold text-gray-800 mb-2">Pagina non trovata</h2>
          <p className="text-sm text-gray-600 mb-4">La pagina che stai cercando non esiste o non è disponibile.</p>
          <button
            onClick={() => window.history.back()}
            className="w-full bg-blue-600 text-white py-2 rounded text-sm font-medium hover:bg-blue-700 transition"
          >
            Torna Indietro
          </button>
        </div>
      </div>
    );
  }

  const handleFavoriteToggle = (hikeId, newFavoriteState) => {
    // Update the local hikes state to reflect the favorite change
    setHikes(prevHikes =>
      prevHikes.map(hike =>
        hike.id === hikeId
          ? { ...hike, is_favorited: newFavoriteState }
          : hike
      )
    );
  };

  const toggleCategory = (category) => {
    setSelectedCategories(prev => {
      const newCategories = prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category];

      // Update URL parameters
      updateURLParams({ categories: newCategories });

      return newCategories;
    });
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    updateURLParams({ search: value });
  };

  const clearSearch = () => {
     setSearchQuery('');
     updateURLParams({ search: '' });
   };

  // Filter hikes based on selected categories and favorites
  let filteredHikes = hikes;

  // Apply category filter (AND logic - must match ALL selected)
  if (selectedCategories.length > 0) {
    filteredHikes = filteredHikes.filter(hike =>
      selectedCategories.every(category =>
        hike.categories?.includes(category)
      )
    );
  }

  // Apply text search filter
  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase().trim();
    filteredHikes = filteredHikes.filter(hike => {
      // Search in title
      if (hike.titolo?.toLowerCase().includes(query)) return true;

      // Search in location
      if (hike.luogo?.toLowerCase().includes(query)) return true;

      // Search in short description
      if (hike.descrizione_breve?.toLowerCase().includes(query)) return true;

      // Search in organizer names
      if (hike.organizers?.some(org =>
        org.display_name?.toLowerCase().includes(query)
      )) return true;

      // Search in tags
      if (hike.tags?.some(tag =>
        tag.toLowerCase().includes(query)
      )) return true;

      // Search in categories
      if (hike.categories?.some(cat =>
        cat.toLowerCase().includes(query)
      )) return true;

      // Search in metadata (distanza, dislivello, etc.)
      if (hike.metadata) {
        const metadataValues = Object.values(hike.metadata);
        if (metadataValues.some(val =>
          val?.toString().toLowerCase().includes(query)
        )) return true;
      }

      // Search by date (Italian month names)
      if (hike.data_inizio) {
        const hikeDate = new Date(hike.data_inizio);
        const hikeMonth = hikeDate.getMonth();
        const hikeYear = hikeDate.getFullYear();

        // Check if query matches an Italian month name
        for (const [monthName, monthIndex] of Object.entries(ITALIAN_MONTHS)) {
          if (monthName.startsWith(query) || query === monthName) {
            if (hikeMonth === monthIndex) return true;
          }
        }

        // Also allow searching by year (e.g., "2025")
        if (query.match(/^\d{4}$/) && parseInt(query) === hikeYear) {
          return true;
        }
      }

      return false;
    });
  }

  // Apply favorites filter using backend-enriched data or AuthContext cache
  if (showOnlyFavorites) {
    filteredHikes = filteredHikes.filter(hike =>
      hike.is_favorited !== undefined ? hike.is_favorited : isFavorite(hike.id)
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Spinner size="md" color="blue" />
          <div className="text-sm text-gray-600">Caricamento gite...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-sm">
          <h2 className="text-lg font-semibold text-red-600 mb-2">Errore</h2>
          <p className="text-sm text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="w-full bg-blue-600 text-white py-2 rounded text-sm font-medium hover:bg-blue-700 transition"
          >
            Torna alla Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-4">
        {/* Compact Header */}
        <div className="mb-4 flex flex-col sm:flex-row justify-between items-center gap-3">
          <div className="flex items-center gap-3">
            <img
              src="/testata.jpg"
              alt="CAI Modena"
              className="rounded shadow h-14 object-contain bg-white"
            />
            <img
              src="/logo_giovani.jpg"
              alt="Giovani CAI"
              className="rounded shadow h-14 object-contain"
            />
            <h1 className="text-lg font-semibold text-gray-800 hidden md:block">
              Gite del Gruppo Giovani
            </h1>
          </div>

          <div className="flex items-center gap-2">
            <a
              href="https://chat.whatsapp.com/C5Muv6lRzrrJJtQIclS5hD"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 py-2 bg-white hover:bg-green-50 border border-gray-200 hover:border-green-400 text-gray-700 hover:text-green-700 rounded-lg shadow-sm hover:shadow transition"
              title="Gruppo WhatsApp"
            >
              <svg
                className="w-5 h-5 flex-shrink-0 text-green-500"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              <span className="text-sm font-medium hidden sm:inline">WhatsApp</span>
            </a>
            <a
              href="https://www.instagram.com/cai_giovani_modena/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 py-2 bg-white hover:bg-pink-50 border border-gray-200 hover:border-pink-400 text-gray-700 hover:text-pink-700 rounded-lg shadow-sm hover:shadow transition"
              title="Instagram"
            >
              <svg
                className="w-5 h-5 flex-shrink-0 text-pink-500"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
              </svg>
              <span className="text-sm font-medium hidden sm:inline">Instagram</span>
            </a>
            <a
              href="https://www.instagram.com/c.a.i_modena/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 py-2 bg-white hover:bg-pink-50 border border-gray-200 hover:border-pink-400 text-gray-700 hover:text-pink-700 rounded-lg shadow-sm hover:shadow transition"
              title="Instagram CAI"
            >
              <svg
                className="w-5 h-5 flex-shrink-0 text-pink-500"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
              </svg>
              <span className="text-sm font-medium hidden sm:inline">Instagram CAI</span>
            </a>
          </div>
        </div>

        {/* Filter Section */}
        <div className="mb-4 bg-white rounded-lg shadow-sm border border-gray-200 p-3">
          {/* Search Input */}
          <div className="mb-3 flex gap-2">
            <div className="relative flex-grow">
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Cerca per titolo, luogo, mese..."
                className="w-full pl-9 pr-9 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition"
              />
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  title="Cancella ricerca"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            {filteredHikes.length > 0 && (
              <button
                onClick={async () => {
                  setCalendarLoading(true);
                  try {
                    const hikeIds = filteredHikes.map(hike => hike.id);
                    const categorySuffix = selectedCategories.length > 0
                      ? '_' + selectedCategories.map(cat =>
                          cat.replace(/[^\w\s-]/g, '').replace(/[\s]+/g, '_')
                        ).join('_')
                      : '';
                    const filename = `Gite_CAI_Modena${categorySuffix}.ics`;
                    const response = await fetch(`${API_URL}/hikes/calendar`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                      },
                      body: JSON.stringify({ hike_ids: hikeIds })
                    });
                    if (!response.ok) throw new Error('Failed to download calendar');
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                  } catch (err) {
                    console.error('Error downloading calendar:', err);
                    setError('Failed to download calendar file');
                  } finally {
                    setCalendarLoading(false);
                  }
                }}
                className={`px-4 py-2 rounded text-sm font-medium transition flex items-center gap-1 whitespace-nowrap ${
                  calendarLoading
                    ? 'bg-gray-200 text-gray-500'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
                disabled={calendarLoading}
              >
                {calendarLoading && <Spinner size="sm" color="white" />}
                <span>{calendarLoading ? '...' : `📅 Aggiungi ${filteredHikes.length} ${filteredHikes.length === 1 ? 'gita' : 'gite'} al calendario`}</span>
              </button>
            )}
          </div>

{/* Filter controls row */}
          <div className="flex flex-wrap items-center gap-2 mb-2">
            {hasPermission('hike:view_hidden:any') && (
              <button
                onClick={() => {
                  const newValue = !showHidden;
                  setShowHidden(newValue);
                  updateURLParams({ hidden: newValue });
                }}
                className={`px-2.5 py-1 rounded text-xs font-medium transition ${
                showHidden
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              >
                {showHidden ? 'Nascoste ON' : 'Nascoste'}
              </button>
            )}
            {user && (
              <button
                onClick={() => {
                  const newValue = !showOnlyFavorites;
                  setShowOnlyFavorites(newValue);
                  updateURLParams({ favorites: newValue });
                }}
                className={`px-2.5 py-1 rounded text-xs font-medium transition ${
                showOnlyFavorites
                    ? 'bg-yellow-400 text-yellow-900'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              >
                {showOnlyFavorites ? 'Preferiti' : 'Preferiti'}
              </button>
            )}
          </div>

          {/* Category pills */}
          <div className="flex flex-wrap gap-1.5">
            {AVAILABLE_CATEGORIES.map(category => (
              <button
                key={category}
                onClick={() => toggleCategory(category)}
                className={`px-2.5 py-1 rounded text-xs font-medium transition ${
                  selectedCategories.includes(category)
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
          {(selectedCategories.length > 0 || searchQuery.trim()) && (
            <button
              onClick={() => {
                setSelectedCategories([]);
                setSearchQuery('');
                updateURLParams({ categories: [], search: '' });
              }}
              className="mt-2 px-2.5 py-1 rounded text-xs font-medium text-red-600 hover:bg-red-50 transition"
            >
              Rimuovi filtri
            </button>
          )}
          {(selectedCategories.length > 0 || searchQuery.trim()) && (
            <p className="mt-2 text-xs text-gray-500">
              {searchQuery.trim() && (
                <span>Cercando "{searchQuery}" </span>
              )}
              {selectedCategories.length > 0 && (
                <span>
                  {searchQuery.trim() ? 'con ' : ''}
                  tutte le categorie selezionate
                </span>
              )}
              {' '}({filteredHikes.length} {filteredHikes.length === 1 ? 'gita' : 'gite'})
            </p>
          )}
        </div>

        {/* Hike Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredHikes.map(hike => (
            <HikeCard
              key={hike.id}
              hike={hike}
              onFavoriteToggle={handleFavoriteToggle}
            />
          ))}
        </div>

        {filteredHikes.length === 0 && !loading && (
          <div className="text-center py-8">
            <p className="text-gray-500 text-sm">
              {searchQuery.trim()
                ? `Nessuna gita trovata per "${searchQuery}".`
                : selectedCategories.length > 0
                  ? 'Nessuna gita trovata con le categorie selezionate.'
                  : 'Nessuna gita disponibile.'}
            </p>
            {(searchQuery.trim() || selectedCategories.length > 0) && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategories([]);
                  updateURLParams({ search: '', categories: [] });
                }}
                className="mt-3 text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Rimuovi tutti i filtri
              </button>
            )}
          </div>
        )}

        <Footer />
      </div>
    </div>
  );
}